import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { ApiStatus, VIEW_CONFIGS, SortConfig } from '../../../../../../utils/constants/commonConstants';
import { getDealFormConfig } from '../../../../../../store/investor/apiHelpers';
import { DEAL_VIEW_TYPES } from '../../../../../../store/investor/constants';
import { DataTableColumn } from '../../../../../../common/_custom';
import DealsRow from './components/DealsRow/DealsRow';
import styles from './DealsTable.module.scss';
import { isLenderType } from '../../../../../../utils/utils';
import { setDealStatusConfig } from '../../../../../../store/investor/action';

type Props = {
    isLoading: boolean;
    emptyScreenComponent: React.ReactNode;
    loadingComponent: React.ReactNode;
    setScrollPos: (scrollPos: number) => void;
    selectedCategory: string;
    sortConfig?: SortConfig;
    onSort?: (field: string) => void;
    currentColumns?: string[];
    onColumnsChange?: (columns: DataTableColumn[]) => void;
    onColumnsConfigChange?: (config: any) => void;
};

export interface HeaderConfig {
    id?: string;
    label?: string;
    greyText?: boolean;
    wrapText?: boolean;
    hideLabel?: boolean;
    type?: string;
    hideRow?: boolean;
}

const DealsTable: React.FC<Props> = ({
    isLoading,
    emptyScreenComponent,
    loadingComponent,
    setScrollPos,
    selectedCategory,
    sortConfig,
    onSort,
    currentColumns = [],
    onColumnsChange,
    onColumnsConfigChange,
}) => {
    const dispatch = useAppDispatch();
    const [mandateHeaders, setMandateHeaders] = useState<DataTableColumn[] | null>(null);
    const [dealsHeaders, setDealsHeaders] = useState<DataTableColumn[] | null>(null);
    const [lenderHeaders, setLenderHeaders] = useState<DataTableColumn[] | null>(null);
    const [configStatus, setConfigStatus] = useState<ApiStatus>(ApiStatus.IDLE);
    const hasNotifiedParent = useRef(false);

    const dealsData = useAppSelector((state) => state.investor.deals);
    const user = useAppSelector((state) => state.user);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const totalDealsCount = dealsData?.dealCount?.totalDeals ?? 0;

    const userType = user.userData.accountType;

    const overlayScreen = useMemo(() => {
        if (isLoading && loadingComponent) return <div className={styles.Loading}>{loadingComponent}</div>;
        else if (totalDealsCount <= 0) if (emptyScreenComponent) return <div className={styles.Loading}>{emptyScreenComponent}</div>;
    }, [isLoading, loadingComponent, emptyScreenComponent, totalDealsCount]);

    const getHeaders = () => {
        if (isLenderType(userType)) return lenderHeaders;
        if (selectedCategory === DEAL_VIEW_TYPES.DEAL_VIEW) return dealsHeaders;
        return mandateHeaders;
    };

    const fetchConfig = async () => {
        try {
            await getDealFormConfig(
                {
                    accountType: user.userData.accountType,
                    investorOrgId: platformData?.investorOrgId,
                    primaryEmail: user.userData?.primaryEmail,
                    configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
                },
                {
                    onSuccess: (configData, orgName) => {
                        setDealsHeaders(configData?.dealViewHeaders ?? []);
                        setMandateHeaders(configData?.mandateHeaders ?? []);
                        setLenderHeaders(configData?.lenderHeaders ?? []);
                        setConfigStatus(ApiStatus.SUCCESS);

                        dispatch(setDealStatusConfig(configData?.dealStatusDropdownOptions || {}));

                        // Notify parent component about column configuration only if it's the first time
                        // to prevent overriding user selections when config is refetched
                        if (onColumnsConfigChange && !hasNotifiedParent.current) {
                            onColumnsConfigChange(configData);
                            hasNotifiedParent.current = true;
                        }
                    },
                },
            );
        } catch (error) {
            console.error('Error fetching deal form config:', error);
            setConfigStatus(ApiStatus.ERROR);
        }
    };

    useEffect(() => {
        if (configStatus === ApiStatus.IDLE && platformData?.investorOrgId && user.userData?.primaryEmail) {
            setConfigStatus(ApiStatus.PENDING);
            fetchConfig();
        }
    }, [platformData, user, configStatus]);

    return (
        <DealsRow
            headers={getHeaders() ?? []}
            rows={dealsData.data}
            selectedCategory={selectedCategory}
            loading={isLoading}
            totalCount={totalDealsCount}
            emptyScreenComponent={emptyScreenComponent}
            sortConfig={sortConfig}
            onSort={onSort}
            dealStatusConfig={dealsData.dealStatusConfig}
            currentColumns={currentColumns}
            onColumnsChange={onColumnsChange}
        />
    );
};

export default DealsTable;
